import { useState } from "react";
import { useAssetCategories, useEmployeeAssets, useAssetStats } from "@/hooks/useAssets";
import { useEmployees } from "@/hooks/useEmployees";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinnerWithText } from "@/components/ui/loading-spinner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Package, 
  Key, 
  Monitor, 
  Plus,
  Search,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Shield,
  Clock
} from "lucide-react";
import { AssetDialog } from "@/components/assets/AssetDialog";
import { AssetsList } from "@/components/assets/AssetsList";
import { AssetStatsCards } from "@/components/assets/AssetStatsCards";
import { AssetTransfersList } from "@/components/assets/AssetTransfersList";

export default function Assets() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  
  const { data: assets = [], isLoading: assetsLoading } = useEmployeeAssets(selectedEmployee);
  const { data: categories = [], isLoading: categoriesLoading } = useAssetCategories();
  const { data: stats, isLoading: statsLoading } = useAssetStats();
  const { employees = [] } = useEmployees();
  
  const loading = assetsLoading || categoriesLoading || statsLoading;
  
  // Filter assets
  const filteredAssets = assets.filter(asset => {
    const matchesSearch = searchQuery === "" || 
      asset.asset_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      asset.asset_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      asset.serial_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      asset.employee?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = filterStatus === "all" || asset.status === filterStatus;
    const matchesType = filterType === "all" || asset.asset_type === filterType;
    
    return matchesSearch && matchesStatus && matchesType;
  });
  
  const handleEditAsset = (asset: any) => {
    setSelectedAsset(asset);
    setIsDialogOpen(true);
  };
  
  const handleAddAsset = () => {
    setSelectedAsset(null);
    setIsDialogOpen(true);
  };
  
  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <LoadingSpinnerWithText text="جاري تحميل العهد..." />
      </div>
    );
  }
  
  return (
    <div className="container mx-auto p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-card-foreground">إدارة العهد</h1>
          <p className="text-muted-foreground mt-1">إدارة عهد الموظفين المادية والرقمية</p>
        </div>
        <Button onClick={handleAddAsset} className="gap-2">
          <Plus className="w-4 h-4" />
          إضافة عهدة جديدة
        </Button>
      </div>
      
      {/* Stats Cards */}
      {stats && <AssetStatsCards stats={stats} />}
      
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="البحث في العهد..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pr-10"
              />
            </div>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="حالة العهدة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الحالات</SelectItem>
                <SelectItem value="assigned">مسلمة</SelectItem>
                <SelectItem value="returned">مُرجعة</SelectItem>
                <SelectItem value="lost">مفقودة</SelectItem>
                <SelectItem value="damaged">تالفة</SelectItem>
                <SelectItem value="expired">منتهية</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger>
                <SelectValue placeholder="نوع العهدة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الأنواع</SelectItem>
                <SelectItem value="physical">مادية</SelectItem>
                <SelectItem value="digital">رقمية</SelectItem>
                <SelectItem value="credentials">بيانات دخول</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={selectedEmployee || "all"} onValueChange={(value) => setSelectedEmployee(value === "all" ? "" : value)}>
              <SelectTrigger>
                <SelectValue placeholder="اختر الموظف" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الموظفين</SelectItem>
                {employees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Tabs */}
      <Tabs defaultValue="assets" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="assets">العهد النشطة</TabsTrigger>
          <TabsTrigger value="history">السجل الكامل</TabsTrigger>
          <TabsTrigger value="transfers">عمليات النقل</TabsTrigger>
        </TabsList>
        
        <TabsContent value="assets" className="space-y-4">
          <AssetsList 
            assets={filteredAssets.filter(a => a.status === "assigned")}
            onEdit={handleEditAsset}
            categories={categories}
          />
        </TabsContent>
        
        <TabsContent value="history" className="space-y-4">
          <AssetsList 
            assets={filteredAssets}
            onEdit={handleEditAsset}
            categories={categories}
            showHistory
          />
        </TabsContent>
        
        <TabsContent value="transfers" className="space-y-4">
          <AssetTransfersList />
        </TabsContent>
      </Tabs>
      
      {/* Asset Dialog */}
      <AssetDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        asset={selectedAsset}
        categories={categories}
        employees={employees}
      />
    </div>
  );
}